import { useState } from "react";
import { useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@/react/contexts";
import { BlockInViewOnlyMode } from "@circle-react/components/ViewOnlyMasquerading/BlockInViewOnlyMode";
import { useWebSocket } from "@circle-react/hooks/useWebSocket";
import { useThemeContext } from "@circle-react/providers/Theme/ThemeContext";
import { Typography } from "@circle-react-uikit/Typography";
import { useUnreadConversations } from "../hooks/useUnreadConversations";

interface FloatingActionButtonProps {
  isOpen: boolean;
  onClick: () => void;
}

const getButtonStyle = (isHovered: boolean) => ({
  background: "white",
  backgroundImage: isHovered
    ? "linear-gradient(180deg, #506CF1 0%, #0F26A6 100%)"
    : "none",
  border: "1px solid var(--Widget-border, rgba(228, 231, 235, 0.40))",
  boxShadow:
    "0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10)",
  transition: "all 0.2s ease-in-out",
});

const getDarkButtonStyle = (isHovered: boolean) => ({
  background: isHovered ? "#FCFAFF" : "#2B2E33",
  color: isHovered ? "black !important" : "white !important",
});

export const FloatingActionButton = ({
  isOpen,
  onClick,
}: FloatingActionButtonProps) => {
  const queryClient = useQueryClient();
  const [isHovered, setIsHovered] = useState(false);
  const { currentCommunityMember } = usePunditUserContext();
  const { isDarkAppearance } = useThemeContext();
  const { data } = useUnreadConversations();
  const unreadCount = data?.unread_count || 0;

  // Determine which icon to show based on hover and theme state
  const getIconConfig = () => {
    if (isHovered && isDarkAppearance) {
      return {
        className: "icon-hw-fab-hover-dark",
        href: "#icon-hw-fab-hover-dark",
      };
    }
    if (isHovered && !isDarkAppearance) {
      return {
        className: "icon-hw-fab-hover",
        href: "#icon-hw-fab-hover",
      };
    }
    if (!isHovered && isDarkAppearance) {
      return {
        className: "icon-hw-fab-default-dark",
        href: "#icon-hw-fab-default-dark",
      };
    }
    return {
      className: "icon-hw-fab-default",
      href: "#icon-hw-fab-default",
    };
  };

  const iconConfig = getIconConfig();

  useWebSocket(
    {
      channel: "HelpWidget::GeneralChannel",
      onMessageReceive: (data: { event: string }) => {
        if (data.event === "reloadConversations") {
          void queryClient.invalidateQueries({
            queryKey: ["help-widget", "conversations", "unread-count"],
          });
        }
      },
      community_member_id: currentCommunityMember?.id,
      canCreateConnection: Boolean(currentCommunityMember?.id),
    },
    [currentCommunityMember?.id],
  );

  return (
    <BlockInViewOnlyMode>
      <div className="relative">
        {unreadCount > 0 && (
          <div
            className="absolute right-0 top-[-4px] inline-flex size-4 flex-col items-center justify-center gap-2 rounded-lg bg-red-600 p-2 shadow-md outline outline-2 outline-white"
            style={{
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.10), 0 2px 4px -2px rgba(0, 0, 0, 0.10)",
            }}
          >
            <div className="justify-start text-xs font-semibold leading-none text-white">
              {unreadCount}
            </div>
          </div>
        )}
        <button
          type="submit"
          className="float-right mb-px flex h-11 items-center justify-center gap-2 self-end rounded-3xl px-4 py-3"
          style={
            isDarkAppearance
              ? getDarkButtonStyle(isHovered)
              : getButtonStyle(isHovered)
          }
          onClick={onClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          aria-label={t("support_widget.floating_button_aria_label")}
          aria-expanded={isOpen}
          aria-controls="support-widget"
        >
          <svg
            className={`icon ${iconConfig.className} duration-400 transition-opacity ease-in-out`}
            aria-hidden="true"
            viewBox="0 0 18 16"
          >
            <use xlinkHref={iconConfig.href} />
          </svg>
          <div className="duration-400 transition-colors ease-in-out">
            <Typography.BodySm
              color={
                isHovered
                  ? isDarkAppearance
                    ? "text-black"
                    : "text-white"
                  : isDarkAppearance
                    ? "text-white"
                    : "text-dark"
              }
              weight="font-semibold"
            >
              {t("help_widget.need_help_fab")}
            </Typography.BodySm>
          </div>
        </button>
      </div>
    </BlockInViewOnlyMode>
  );
};
